import React from 'react';
import {
  Box,
  Typography,
  Button,
  useTheme
} from '@mui/material';
import {
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { CHOLA_COLORS } from '@/constants/filterStyles';

interface FilterSectionHeaderProps {
  expanded: boolean;
  onToggle: () => void;
  appliedCount: number;
  onClearAll?: () => void;
  hasChanges?: boolean;
  onApply?: () => void;
  compact?: boolean;
  title?: string;
}

export function FilterSectionHeader({
  expanded,
  onToggle,
  appliedCount,
  onClearAll,
  hasChanges = false,
  onApply,
  compact = false,
  title = 'Filters'
}: FilterSectionHeaderProps) {
  const theme = useTheme();
  const hasActiveFilters = appliedCount > 0;

  return (
    <Box 
      sx={{ 
        p: compact ? '8px 12px' : 2, 
        display: 'flex', 
        alignItems: 'center',
        cursor: 'pointer',
        bgcolor: expanded ? CHOLA_COLORS.primary : 'background.paper',
        color: expanded ? 'white' : 'text.primary',
        borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px ${expanded ? 0 : theme.shape.borderRadius}px ${expanded ? 0 : theme.shape.borderRadius}px`,
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: expanded ? CHOLA_COLORS.hover : CHOLA_COLORS.grey
        },
        minHeight: compact ? '32px' : '48px'
      }}
      onClick={onToggle}
    >
      <FilterIcon sx={{ 
        mr: 1, 
        color: expanded ? 'white' : CHOLA_COLORS.primary,
        fontSize: compact ? '0.8125rem' : '1.125rem' // Adjusted for 150% zoom
      }} />
      <Typography 
        variant={compact ? "subtitle2" : "h6"} 
        component="h2" 
        sx={{ 
          flexGrow: 1,
          fontSize: compact ? '0.7rem' : undefined, // Increased from 0.75rem to 0.7rem for better readability at 150% zoom
          fontWeight: compact ? 500 : 600
        }}
      >
        {title} {hasActiveFilters && `(${appliedCount} applied)`}
        {hasChanges && (
          <Box 
            component="span" 
            sx={{ 
              display: 'inline-block',
              ml: 1,
              px: 0.5,
              py: 0.1,
              borderRadius: '8px',
              fontSize: compact ? '0.65rem' : '0.75rem', // Increased from 0.5rem/0.7rem for better readability at 150% zoom
              bgcolor: expanded ? 'rgba(255, 255, 255, 0.2)' : CHOLA_COLORS.secondary,
              color: 'white',
              fontWeight: 'bold'
            }}
          >
            Changes pending
          </Box>
        )}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 0.5 : 1 }}>
        {hasActiveFilters && onClearAll && (
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onClearAll();
            }}
            variant="outlined"
            sx={{ 
              mr: compact ? 0.5 : 1,
              color: expanded ? 'white' : CHOLA_COLORS.primary,
              borderColor: expanded ? 'white' : CHOLA_COLORS.primary,
              '&:hover': {
                borderColor: expanded ? 'white' : CHOLA_COLORS.primary,
                bgcolor: expanded ? 'rgba(255, 255, 255, 0.1)' : CHOLA_COLORS.grey
              },
              ...(compact && {
                fontSize: '0.7rem', // Increased from 0.6rem for better readability at 150% zoom
                py: 0.2,
                px: 0.5,
                minWidth: 'auto'
              })
            }}
          >
            Clear All
          </Button>
        )}
        {hasChanges && !expanded && onApply && (
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onApply();
            }}
            variant="contained"
            sx={{ 
              mr: compact ? 0.5 : 1,
              bgcolor: CHOLA_COLORS.secondary,
              '&:hover': {
                bgcolor: '#c01229' // darker red
              },
              ...(compact && {
                fontSize: '0.7rem', // Increased from 0.6rem for better readability at 150% zoom
                py: 0.2,
                px: 0.5,
                minWidth: 'auto'
              })
            }}
          >
            Apply
          </Button>
        )}
        {expanded ? (
          <ExpandLessIcon sx={{ 
            color: expanded ? 'white' : CHOLA_COLORS.primary,
            fontSize: compact ? '0.8125rem' : '1.125rem' // Adjusted for 150% zoom
          }} />
        ) : (
          <ExpandMoreIcon sx={{ 
            color: expanded ? 'white' : CHOLA_COLORS.primary,
            fontSize: compact ? '0.8125rem' : '1.125rem' // Adjusted for 150% zoom
          }} />
        )}
      </Box>
    </Box>
  );
}

